import { UseFormRegisterReturn } from "react-hook-form"
import {
  StyledPercentageInput,
  PercentageInputContainer,
  PercentageSuffix,
} from "./PercentageInput.styled"

interface PercentageInputProps {
  /**
   * The React Hook Form register return object
   */
  registerReturn: UseFormRegisterReturn
  /**
   * Optional placeholder text
   */
  placeholder?: string
  /**
   * Number of decimal places to show (default: 2)
   */
  decimalPlaces?: number
  /**
   * Minimum percentage value (default: 0)
   */
  min?: number
  /**
   * Maximum percentage value (default: 100)
   */
  max?: number
}

/**
 * Generic PercentageInput component for React Hook Form.
 * Displays value as percentage (e.g., 5%) but stores as decimal (0.05)
 * when used with appropriate setValueAs in the register options.
 *
 * @example
 * ```tsx
 * <PercentageInput
 *   registerReturn={register("taxRate", {
 *     required: "Tax rate is required",
 *     setValueAs: value => {
 *       const numValue = parseFloat(value);
 *       return isNaN(numValue) ? null : (numValue / 100).toFixed(4);
 *     }
 *   })}
 * />
 * ```
 */
export const PercentageInput = ({
  registerReturn,
  placeholder,
  decimalPlaces = 2,
  min = 0,
  max = 100,
}: PercentageInputProps) => {
  return (
    <PercentageInputContainer alignItems="center">
      <StyledPercentageInput
        type="number"
        placeholder={placeholder}
        min={min}
        max={max}
        step={Math.pow(10, -decimalPlaces)} // Step based on decimal places
        {...registerReturn}
      />
      <PercentageSuffix>%</PercentageSuffix>
    </PercentageInputContainer>
  )
}
